/**
 * PlaylistDataContext — exposes the active playlist's channels + EPG to all
 * player pages without making any backend Content/EPG calls.
 *
 * The active playlist id is owned by PlayerLayout (loaded from
 * /api/player/playlists which returns the {id, name, m3u_url, epg_url} list).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { loadPlaylist, reloadPlaylist, loadPlaylistFromCacheOnly } from "./playlistCache";
import { debugLog } from "./debugMode";
import { hydrateFromPrewarmOnce } from "./prewarmHydrate";
import { startSeriesPosterWarm, stopSeriesPosterWarm } from "./posterWarmer";

// v1.3.43 — Cold-start auto refresh, with safeguards against OOM on
// huge (500k+ channel) playlists.
//
// The first version (v1.3.42) just unconditionally called
// reloadPlaylist() on every cold start. That worked great on small
// catalogs, but on the field it crashed Smart TVs during the B2C
// metadata overlay step (multi-MB JSON merge across 15k+ series). The
// user reported the crash with a debug-overlay video — last log line
// was "B2C overlay: fetching catalog for metadata enrichment".
//
// New strategy:
//   1. Throttle per playlist: only auto-refresh if 12h+ have passed
//      since the last successful force refresh. Recent users still
//      get fresh content (typical usage = open at least once a day),
//      but a re-open within minutes never pays the cost twice.
//   2. Use loadPlaylist({force:true}) instead of reloadPlaylist():
//      the IDB cache is preserved, so the HEAD/ETag short-circuit
//      inside loadPlaylist can return "upstream unchanged → use
//      cache" in ~200ms when the provider hasn't moved. That's the
//      common case after the first refresh of the day.
//   3. Module-level Set still tracks per-process so we don't fire
//      multiple times within one app session.
const AUTO_REFRESH_INTERVAL_MS = 12 * 60 * 60 * 1000; // 12h
const LAST_AUTO_REFRESH_KEY = (id) => `vupp_last_auto_refresh_at__${id}`;
const _refreshedThisSession = new Set();

function _shouldColdStartRefresh(playlistId, playlistSnapshotEnabled = true) {
  if (_refreshedThisSession.has(playlistId)) return false;
  // v1.3.65 — When the snapshot is DISABLED for this playlist, every
  // cold start has to talk to the provider directly. We can't afford
  // to serve a 12h-old IndexedDB cache or new episodes will be
  // invisible until tomorrow. The 12h gate stays in place for the
  // snapshot path because there the snapshot itself is the freshness
  // gate (managed by the server scheduler).
  if (playlistSnapshotEnabled === false) return true;
  try {
    const raw = localStorage.getItem(LAST_AUTO_REFRESH_KEY(playlistId));
    const last = raw ? parseInt(raw, 10) : 0;
    if (Number.isFinite(last) && Date.now() - last < AUTO_REFRESH_INTERVAL_MS) {
      return false;
    }
  } catch { /* localStorage blocked → fall through and refresh */ }
  return true;
}

function _stampAutoRefresh(playlistId) {
  _refreshedThisSession.add(playlistId);
  try {
    localStorage.setItem(LAST_AUTO_REFRESH_KEY(playlistId), String(Date.now()));
  } catch { /* ignore */ }
}

const PlaylistDataContext = createContext(null);

export function PlaylistDataProvider({ playlist, children }) {
  const [data, setData] = useState({
    channels: [],
    epgByChannel: {},
    xtream: null,
    fetched_at: 0,
    source: "empty",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState(null);

  const refresh = useCallback(
    async (force = false) => {
      if (!playlist?.id || !playlist?.m3u_url) {
        setData({ channels: [], epgByChannel: {}, xtream: null, fetched_at: 0, source: "empty" });
        setStatus("ready");
        return;
      }
      // v1.3.43 — Cold-start auto refresh with 12h throttle and
      // HEAD/ETag short-circuit (see top-of-file comment).
      const shouldAutoRefresh = _shouldColdStartRefresh(
        playlist.id,
        playlist.playlist_snapshot_enabled,
      );
      const shouldForce = force || shouldAutoRefresh;
      if (shouldAutoRefresh) {
        _stampAutoRefresh(playlist.id);
        debugLog(
          `[PlaylistDataContext] cold-start auto refresh id=${playlist.id} (12h gate passed)`,
        );
      }
      setStatus("loading");
      setError(null);
      try {
        // Manual reload: clear caches + force fetch (reloadPlaylist).
        // Auto cold-start refresh: keep IDB cache so the HEAD/ETag
        // short-circuit inside loadPlaylist({force:true}) can return
        // the cache in ~200ms when upstream hasn't changed — which
        // avoids the B2C metadata-overlay OOM on huge catalogs.
        // v1.3.65 — when the snapshot is DISABLED we use reloadPlaylist
        // even on auto refresh so the HEAD/ETag short-circuit can't
        // serve a stale cache. Providers that don't set proper Last-
        // Modified headers (most of them) would otherwise hide new
        // episodes for the whole 12h gate window.
        // v1.4.24 — Manual reload: aggressively free the previous
        // channels array in state BEFORE calling `reloadPlaylist` so
        // the WebView isn't holding two 400 k-item arrays at once
        // during parse+write. Combined with the chunked IDB wipe
        // (see `idbPutPlaylist`) this drops peak native memory from
        // ~400 MB to ~50 MB on the "manual update" path.
        let result;
        const snapshotOff = playlist.playlist_snapshot_enabled === false;
        if (force || (shouldForce && snapshotOff)) {
          if (force) {
            // v1.4.21 — release the old 400 k-item channels array BEFORE
            // parsing the new one, but keep the object shape intact
            // (`setData(null)` crashed the tree with "Cannot read
            // properties of null (reading 'channels')" while the manual
            // reload was in-flight).
            setData({
              channels: [],
              epgByChannel: {},
              xtream: null,
              b2c: null,
              fetched_at: 0,
              source: "empty",
            });
            // Yield so React can flush the state update and the GC
            // can reclaim the old 400 k channels array before we
            // start parsing the new one.
            await new Promise((r) => setTimeout(r, 50));
          }
          result = await reloadPlaylist(playlist);
        } else if (shouldForce) {
          // Auto cold-start refresh — pass autoRefresh:true so the
          // B2C overlay step is skipped on huge catalogs (memory guard).
          result = await loadPlaylist(playlist, { force: true, autoRefresh: true });
        } else {
          result = await loadPlaylist(playlist);
        }
        setData(result);
        setStatus("ready");
        // v1.3.0 — Kick off the background TMDB poster warmer for the
        // Series tiles table. Runs at idle priority and cancels itself
        // on the next playlist switch, so it never fights with the
        // user's interactions.
        if (playlist?.id) startSeriesPosterWarm(playlist.id);
      } catch (e) {
        setError(e?.message || "Failed to load playlist");
        setStatus("error");
      }
    },
    [playlist?.id, playlist?.m3u_url, playlist?.epg_url]
  );

  // Reload whenever the active playlist changes
  useEffect(() => {
    refresh(false);
    return () => {
      // v1.3.0 — Cancel any in-flight background poster warm when the
      // active playlist changes, so we don't fight the new playlist's
      // fetch for network capacity.
      if (playlist?.id) stopSeriesPosterWarm(playlist.id);
    };
  }, [refresh, playlist?.id]);

  // v1.3.67 — Xtream catalog pre-warm hydration (ITVZA-inspired
  // "instant cold start"). Fires once per cold start when the
  // playlist has Xtream credentials so the bundle exists.
  // Fire-and-forget — the lazy refresh effects in LiveTv.jsx /
  // Browse.jsx remain the authority on freshness; this just seeds
  // IDB so their FIRST read after launch hits warm cache instead
  // of paying for a network round trip per category opened.
  //
  // v1.3.67.1 — removed the manual fetch + header building. The
  // pre-warm endpoint requires the FULL device-auth header set
  // (X-Device-ID + X-Activation-Key + X-Platform + X-App-Version)
  // which playerAxios attaches automatically via its interceptor.
  // Hand-rolling only X-Activation-Key produced a 401 on prod.
  useEffect(() => {
    if (!data?.xtream?.creds || !playlist?.id) return;
    hydrateFromPrewarmOnce(playlist.id, data.xtream.creds)
      .then((res) => {
        debugLog(
          `[PlaylistDataContext] prewarm hydrate: ${res?.status} ` +
          (res?.item_count != null ? `(${res.item_count} items)` : ""),
        );
      })
      .catch(() => { /* silent — already logged inside the helper */ });
  }, [playlist?.id, data?.xtream]);

  // v1.3.56 — Hourly background auto-refresh of the active playlist so
  // newly added movies / series / channels appear without the user
  // having to restart the app or pull-to-refresh. Uses the same
  // HEAD/ETag short-circuit `loadPlaylist({force:true, autoRefresh:true})`
  // path as the cold-start refresh — when the provider hasn't changed
  // upstream this returns in <200ms with zero network cost. When
  // content HAS changed, the channel list rebuilds and Browse re-
  // renders with the new items in place.
  //
  // The B2C metadata-overlay step is SKIPPED in autoRefresh:true mode
  // so we never re-OOM a Smart TV in the background — fresh metadata
  // is only fetched on the next cold start (12h gate).
  useEffect(() => {
    if (!playlist?.id || !playlist?.m3u_url) return undefined;
    const HOUR_MS = 60 * 60 * 1000;
    const t = setInterval(() => {
      // Skip while the previous refresh is still running so we don't
      // pile two large catalog parses on top of each other.
      setStatus((cur) => {
        if (cur === "loading") return cur;
        debugLog(
          `[PlaylistDataContext] hourly auto-refresh id=${playlist.id}`,
        );
        // Fire the refresh in the background — we don't await; the
        // existing refresh() flips status to "loading" then "ready".
        // eslint-disable-next-line no-use-before-define
        loadPlaylist(playlist, { force: true, autoRefresh: true })
          .then((result) => {
            setData(result);
            setStatus("ready");
          })
          .catch((e) => {
            // Don't surface hourly refresh failures to the user — the
            // existing in-memory catalog stays intact, we'll retry in
            // an hour. Just log for diagnostics.
            debugLog(
              `[PlaylistDataContext] hourly auto-refresh failed: ${e?.message || e}`,
            );
            setStatus("ready");
          });
        return "loading";
      });
    }, HOUR_MS);
    return () => clearInterval(t);
  }, [playlist?.id, playlist?.m3u_url, playlist?.epg_url, playlist]);

  const value = {
    playlist,
    channels: data?.channels || [],
    epgByChannel: data?.epgByChannel || {},
    xtream: data?.xtream || null,
    b2c: data?.b2c || null, // { base, token } when source==="b2c" — used by the
    // series drill-down to lazy-fetch episodes on tile open.
    fetched_at: data?.fetched_at || 0,
    source: data?.source || "empty",
    status,
    error,
    reload: () => refresh(true),
  };

  return (
    <PlaylistDataContext.Provider value={value}>{children}</PlaylistDataContext.Provider>
  );
}

export function usePlaylistData() {
  const ctx = useContext(PlaylistDataContext);
  if (!ctx) {
    // Default no-op context so consumers don't crash outside a provider
    return {
      playlist: null,
      channels: [],
      epgByChannel: {},
      xtream: null,
      b2c: null,
      fetched_at: 0,
      status: "idle",
      error: null,
      refresh: async () => {},
    };
  }
  return ctx;
}